import { For, Show, createEffect, createMemo, createSignal, onCleanup, onMount } from 'solid-js';
import { createStore, produce, reconcile, unwrap } from 'solid-js/store';
import Text from '../components/Text';
import ItemStackView from '../components/ItemStackView';
import ItemIdentifierPicker from '../components/ItemIdentifierPicker';
import LayerPanel, { TEXT_FONTS } from '../components/mapart/LayerPanel';
import MapartCanvas, { type CanvasView, type ResizeHandle, type SelectionBox } from '../components/mapart/MapartCanvas';
import PalettePicker from '../components/mapart/PalettePicker';
import PresetDialog from '../components/mapart/PresetDialog';
import IconDialog from '../components/mapart/IconDialog';
import Modal from '../components/Modal';
import { EmptyState } from '../components/EmptyState';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { useItemName } from '../hooks/useItemName';
import { formatDateTime } from '../services/dateFormat';
import { saveBlob } from '../services/downloadFile';
import { useCraftingResolver } from '../services/craftingResolverContext';
import { fromIdentifier } from '../services/itemRefAdapters';
import { MapartWorkerClient } from '../mapart/MapartWorkerClient';
import {
    DEFAULT_EDGE_CUTOFF,
    DEFAULT_FONT_WEIGHT,
    DEFAULT_TEXT_EDGES,
    History,
    ImageRegistry,
    MAX_LAYER_SCALE,
    TRANSPARENT,
    composite as compositeDocument,
    defaultLayerSection,
    documentLength,
    documentWidth,
    emptyDocument,
    expandGroups,
    fittingPixelScale,
    gradientPaint,
    groupLayers,
    isUniversalPatch,
    layerBox,
    nextLayerId,
    noPaint,
    normaliseDocument,
    pruneGroups,
    remapGroups,
    reorderBlock,
    reserveLayerIds,
    settlePixelPerfect,
    ungroupLayers,
    solidPaint,
    textWidth,
    type Layer,
    type MapartDocument,
    type TextLayer,
} from '../mapart/document';
import { buildPaletteEntries, defaultSelection, loadPalette, selectionForPreset } from '../mapart/palette';
import { NEUTRAL_ADJUSTMENTS } from '../mapart/imagePrep';
import { MAP_SIZE, cropArea } from '../mapart/pipeline';
import { SNAP_SCREEN_PX, boxEdges, frameTargets, snapAxis, type SnapAxes } from '../mapart/snapping';
import {
    PROJECT_FILE_EXTENSION,
    highestLayerCounter,
    newProjectId,
    parseProject,
    serialiseProject,
    type MapartProject,
    type ProjectSummary,
} from '../mapart/project';
import {
    deleteProject,
    lastOpenedId,
    listProjects,
    loadProject,
    rememberLastOpened,
    saveProject,
} from '../mapart/projectStore';
import {
    PRESET_FILE_EXTENSION,
    loadPresets,
    parsePresets,
    removePreset,
    enabledCount,
    sanitiseSelection,
    serialisePresets,
    storePresets,
    upsertPreset,
    type BlockPreset,
} from '../mapart/presets';
import { DITHER_METHODS } from '../mapart/quantize';
import {
    DEFAULT_VIEWPORT,
    fit,
    normaliseViewport,
    zoomAt,
    type Viewport,
} from '../mapart/viewport';
import { defaultSettings, normaliseSettings } from '../mapart/settings';
import { buildSchematicLayout, encodeSchematic } from '../mapart/schematic';
import { buildPreviewMesh } from '../mapart/previewMesh';
import { zipStore, type ZipEntry } from '../mapart/zip';
import type { BlockSelection, BuildRegion, BuildResult, ColourMetric, ExportMode, MapartPalette, MapartSettings, MaterialCount, SectionMap, StaircaseMode, SupportMode } from '../mapart/types';
import styles from './MapartEditor.module.css';

const MAX_MAPS = 100;

/** Above this the dense MCEdit arrays stop fitting in a browser tab. */
const MAX_SCHEMATIC_BYTES = 192 * 1024 * 1024;

/** Grab tolerance in artwork pixels, so thin text is still easy to catch. */
const PICK_PADDING = 3;

/** Marks the system clipboard as holding a copy made in here, so a paste knows which source wins. */
const CLIPBOARD_TOKEN = 'vektor.mapart.layers:';

/** Offset a pasted or duplicated layer so it does not hide behind the original. */
const PASTE_OFFSET = 8;

/** One click of the zoom buttons. Small enough to land on a wanted magnification. */
const ZOOM_STEP = 1.25;

/** Breathing room around a fitted artwork, so its edges are not flush against the border. */
const FIT_PADDING = 12;

const NUDGE_KEYS: Record<string, [number, number]> = {
    ArrowLeft: [-1, 0],
    ArrowRight: [1, 0],
    ArrowUp: [0, -1],
    ArrowDown: [0, 1],
};

/** Shift covers ground faster, since a block at a time is slow across a full map. */
const NUDGE_COARSE = 10;


export default function MapartEditor() {
    const { t } = useTranslation();
    const toast = useToast();
    const resolver = useCraftingResolver();
    const { stackName } = useItemName();

    const [palette, setPalette] = createSignal<MapartPalette | null>(null);
    const [doc, setDoc] = createStore<MapartDocument>(emptyDocument());
    const [settings, setSettings] = createStore<MapartSettings>(defaultSettings());
    const [result, setResult] = createSignal<BuildResult | null>(null);
    // Last entry is the primary: the one whose values the property editors show.
    const [selectedIds, setSelectedIds] = createSignal<string[]>([]);
    const [revision, setRevision] = createSignal(0);
    const [compositeRevision, setCompositeRevision] = createSignal(0);
    const [viewport, setViewport] = createSignal<Viewport>({ ...DEFAULT_VIEWPORT });
    const [view, setView] = createSignal<CanvasView>('both');
    const [leftOpen, setLeftOpen] = createSignal(true);
    const [rightOpen, setRightOpen] = createSignal(true);
    const [name, setName] = createSignal('mapart');
    const [busy, setBusy] = createSignal(false);
    const [exporting, setExporting] = createSignal(false);
    const [loading, setLoading] = createSignal(true);
    const [canUndo, setCanUndo] = createSignal(false);
    const [canRedo, setCanRedo] = createSignal(false);

    const [projectId, setProjectId] = createSignal(newProjectId());
    const [recents, setRecents] = createSignal<ProjectSummary[]>([]);
    const [recentsOpen, setRecentsOpen] = createSignal(false);
    const [presets, setPresets] = createSignal<BlockPreset[]>([]);
    const [presetsOpen, setPresetsOpen] = createSignal(false);
    const [iconsOpen, setIconsOpen] = createSignal(false);
    const [guides, setGuides] = createSignal<{ x: number | null; y: number | null }>({ x: null, y: null });
    const [clipboardLayers, setClipboardLayers] = createSignal<Layer[]>([]);

    const registry = new ImageRegistry();
    const history = new History();
    const composite = document.createElement('canvas');
    let client: MapartWorkerClient | null = null;
    let buildTimer: number | undefined;
    let saveTimer: number | undefined;
    let nudgeTimer: number | undefined;
    let projectInput: HTMLInputElement | undefined;
    let presetInput: HTMLInputElement | undefined;
    /** Reported by the canvas, so fitting and centred zooming do not have to measure the DOM here. */
    let surfaceWidth = 0;
    let surfaceHeight = 0;
    let compositeFrame = 0;
    /** Unsnapped position of the selection box during a drag, plus where that drag started. */
    let dragRaw: { x: number; y: number; fromX: number; fromY: number } | null = null;
    /** Unsnapped scale during a resize, or a pixel perfect image could never leave its current step. */
    let resizeRawScale: number | null = null;
    /** Rebuilt with the composite: which layer's build section owns each pixel. */
    const sectionMap: SectionMap = { owners: new Uint8Array(0), sections: [] };
    /** What a click inside the current selection should shrink it down to: one layer, or one group. */
    let collapseTo: string[] | null = null;
    /** Set by a copy in here, cleared when the window loses focus, which is the only moment a copy
     * could have happened somewhere else. Covers the case where writing the clipboard is denied. */
    let ownCopyIsNewest = false;
    let gestureOpen = false;
    let fileInput: HTMLInputElement | undefined;

    const bump = () => setRevision((v) => v + 1);
    const syncHistory = () => {
        setCanUndo(history.canUndo);
        setCanRedo(history.canRedo);
    };
    const snapshot = () => {
        history.push(unwrap(doc));
        syncHistory();
    };
    const beginGesture = () => {
        if (gestureOpen) return;
        snapshot();
        gestureOpen = true;
    };
    const endGesture = () => {
        gestureOpen = false;
    };

    const scheduleBuild = () => {
        window.clearTimeout(buildTimer);
        buildTimer = window.setTimeout(() => void runBuild(), 80);
    };

    /** Pointer moves arrive faster than frames; one composite per frame is enough. */
    const scheduleComposite = () => {
        if (compositeFrame) return;
        compositeFrame = requestAnimationFrame(() => {
            compositeFrame = 0;
            drawComposite();
        });
    };

    const flushComposite = () => {
        if (!compositeFrame) return;
        cancelAnimationFrame(compositeFrame);
        compositeFrame = 0;
        drawComposite();
    };

    /** Cheap enough to run on every pointer move, unlike the quantise pass behind it. */
    const drawComposite = () => {
        const width = documentWidth(doc);
        const length = documentLength(doc);
        if (composite.width !== width || composite.height !== length) {
            composite.width = width;
            composite.height = length;
        }
        const ctx = composite.getContext('2d');
        if (!ctx) return null;
        if (sectionMap.owners.length !== width * length) sectionMap.owners = new Uint8Array(width * length);
        compositeDocument(unwrap(doc), registry, ctx, sectionMap);
        setCompositeRevision((v) => v + 1);
        return ctx;
    };

    const runBuild = async () => {
        const active = palette();
        if (!active || !client) return;
        flushComposite();
        const ctx = composite.getContext('2d');
        if (!ctx) return;
        const width = documentWidth(doc);
        const length = documentLength(doc);
        const image = ctx.getImageData(0, 0, width, length);
        setBusy(true);
        const built = await client.build(image.data, width, length, unwrap(settings), sectionMap);
        setBusy(false);
        setResult(built);
    };

    /** An empty recents list has to mean empty, not that reading the store quietly failed. */
    const refreshRecents = async () => {
        try {
            setRecents(await listProjects());
        } catch {
            toast?.error(t('MAPART_PROJECT_LIST_FAILED'));
        }
    };

    const openStored = async (id: string) => {
        try {
            return await loadProject(id);
        } catch {
            toast?.error(t('MAPART_PROJECT_LOAD_FAILED'));
            return null;
        }
    };
    const applyProject = async (project: MapartProject) => {
        registry.clear();
        for (const [sourceId, blob] of Object.entries(project.images)) {
            registry.set(sourceId, await createImageBitmap(blob), blob);
        }
        reserveLayerIds(highestLayerCounter(project.doc));
        setProjectId(project.id);
        setName(project.name);
        setDoc(reconcile(normaliseDocument(project.doc)));
        setSettings(reconcile(normaliseSettings(project.settings)));
        setViewport(normaliseViewport(project.viewport));
        setSelectedIds([]);
        rememberLastOpened(project.id);
        bump();
    };

    /** Only the sources the document still points at, so deleting a layer eventually reclaims space. */
    const usedImages = () => {
        const used = new Set(
            unwrap(doc).layers.flatMap((layer) => (layer.kind === 'image' ? [layer.sourceId] : [])),
        );
        return Object.fromEntries(
            Object.entries(registry.blobs()).filter(([sourceId]) => used.has(sourceId)),
        );
    };

    const currentProject = (): MapartProject => ({
        id: projectId(),
        name: name().trim() || t('MAPART_UNTITLED'),
        updatedAt: new Date().toISOString(),
        doc: structuredClone(unwrap(doc)),
        settings: structuredClone(unwrap(settings)),
        images: usedImages(),
        viewport: { ...viewport() },
    });

    const scheduleAutosave = () => {
        window.clearTimeout(saveTimer);
        saveTimer = window.setTimeout(() => {
            void saveProject(currentProject())
                .then(() => rememberLastOpened(projectId()))
                .catch(() => toast?.error(t('MAPART_PROJECT_SAVE_FAILED')));
        }, 700);
    };

    onMount(() => {
        void (async () => {
            const loaded = await loadPalette();
            setSettings('selection', reconcile(defaultSelection(loaded)));
            setPalette(loaded);
            client = new MapartWorkerClient(loaded);

            const previous = lastOpenedId();
            if (previous) {
                const stored = await openStored(previous);
                if (stored) await applyProject(stored);
            }
            await refreshRecents();
            setPresets(loadPresets());

            setLoading(false);
            bump();
        })();

        window.addEventListener('paste', onPaste);
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('blur', onWindowBlur);
    });

    onCleanup(() => {
        window.removeEventListener('paste', onPaste);
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('blur', onWindowBlur);
        window.clearTimeout(buildTimer);
        window.clearTimeout(saveTimer);
        if (compositeFrame) cancelAnimationFrame(compositeFrame);
        client?.dispose();
    });

    createEffect(() => {
        revision();
        scheduleComposite();
        scheduleBuild();
        if (!loading()) scheduleAutosave();
    });

    // The name and the viewport live outside the document, so they bump no revision and the effect
    // above would never write them back. Panning deliberately stays off that path: it is not an edit,
    // so it must not rebuild the artwork, only be remembered.
    createEffect(() => {
        name();
        viewport();
        if (!loading()) scheduleAutosave();
    });

    /**
     * Only a field you type into owns the clipboard and the arrow keys. A slider, dropdown or
     * checkbox keeps focus after you use it, and swallowing the shortcuts there would mean copying
     * a shape stops working right after you edited one of its properties.
     */
    const TYPING_INPUTS = ['text', 'search', 'url', 'email', 'tel', 'password', 'number'];

    const isEditingField = (target: EventTarget | null) => {
        const element = target as HTMLElement | null;
        if (!element) return false;
        if (element.isContentEditable || element.tagName === 'TEXTAREA') return true;
        return element.tagName === 'INPUT' && TYPING_INPUTS.includes((element as HTMLInputElement).type);
    };

    const onKeyDown = (event: KeyboardEvent) => {
        if (isEditingField(event.target)) return;

        if (event.key === 'Delete' || event.key === 'Backspace') {
            if (selectedIds().length === 0) return;
            event.preventDefault();
            void deleteSelected();
            return;
        }
        if (event.key === 'Escape') {
            if (selectedIds().length === 0) return;
            event.preventDefault();
            setSelectedIds([]);
            return;
        }

        const nudge = NUDGE_KEYS[event.key];
        if (nudge && !event.ctrlKey && !event.metaKey) {
            if (selectedIds().length === 0) return;
            event.preventDefault();
            const step = event.shiftKey ? NUDGE_COARSE : 1;
            nudgeSelection(nudge[0] * step, nudge[1] * step);
            return;
        }

        if (!(event.ctrlKey || event.metaKey)) return;
        const key = event.key.toLowerCase();

        if (key === 'z') {
            event.preventDefault();
            if (event.shiftKey) redo();
            else undo();
            return;
        }
        if (key === 'y') {
            event.preventDefault();
            redo();
            return;
        }
        if (key === 'a') {
            event.preventDefault();
            setSelectedIds(doc.layers.map((layer) => layer.id));
            return;
        }
        if (key === 'c') {
            event.preventDefault();
            copySelected();
            return;
        }
        if (key === 'd') {
            event.preventDefault();
            pasteLayers(selectedRawLayers());
            return;
        }
        if (key === 'g') {
            event.preventDefault();
            if (event.shiftKey) ungroupSelection();
            else groupSelection();
        }
    };

    /** Store copies rather than the reactive proxies, so a later edit cannot rewrite the clipboard. */
    const selectedRawLayers = () => {
        const ids = new Set(selectedIds());
        return unwrap(doc).layers.filter((layer) => ids.has(layer.id));
    };

    const copySelected = () => {
        const raw = selectedRawLayers();
        if (raw.length === 0) return;
        setClipboardLayers(raw.map((layer) => structuredClone(layer)));
        // Claim the system clipboard as well. The layers themselves stay in memory, since an image
        // layer points at a bitmap only this page holds, but whatever was on the clipboard before
        // has to go: otherwise the next paste hands us that instead of what was just copied.
        void navigator.clipboard?.writeText(`${CLIPBOARD_TOKEN}${raw.length}`).catch(() => undefined);
        ownCopyIsNewest = true;
        toast?.info(
            raw.length === 1
                ? t('MAPART_LAYER_COPIED', raw[0].name)
                : t('MAPART_LAYERS_COPIED', raw.length),
        );
    };

    /** Image copies share the source bitmap, so deleting one layer must not free it for the other. */
    const pasteLayers = (sources: Layer[]) => {
        if (sources.length === 0) return;
        snapshot();
        endGesture();
        // Fresh group ids, or the copies would join the group they were copied from. A copy of a
        // single member is then left alone in its group, which pruning clears.
        const copies = pruneGroups(
            remapGroups(
                sources.map((source) => ({
                    ...structuredClone(source),
                    id: nextLayerId(),
                    x: source.x + PASTE_OFFSET,
                    y: source.y + PASTE_OFFSET,
                })),
            ),
        );
        setDoc('layers', (layers) => [...layers, ...copies]);
        setSelectedIds(copies.map((layer) => layer.id));
        bump();
    };

    const onWindowBlur = () => {
        ownCopyIsNewest = false;
    };

    const onPaste = (event: ClipboardEvent) => {
        if (isEditingField(event.target)) return;
        // Our own token wins over any image on the clipboard: it is only there if the last copy
        // happened in here, and a later copy from anywhere else replaces it.
        if (ownCopyIsNewest || event.clipboardData?.getData('text/plain').startsWith(CLIPBOARD_TOKEN)) {
            pasteLayers(clipboardLayers());
            return;
        }
        const file = [...(event.clipboardData?.items ?? [])]
            .find((item) => item.type.startsWith('image/'))
            ?.getAsFile();
        if (file) {
            void addImageLayer(file, file.name || t('MAPART_LAYER_PASTED'));
            return;
        }
        pasteLayers(clipboardLayers());
    };

    const undo = () => {
        const restored = history.undo(structuredClone(unwrap(doc)));
        syncHistory();
        if (!restored) return;
        setDoc(reconcile(restored));
        bump();
    };

    const redo = () => {
        const restored = history.redo(structuredClone(unwrap(doc)));
        syncHistory();
        if (!restored) return;
        setDoc(reconcile(restored));
        bump();
    };

    const newProject = async () => {
        if (doc.layers.length > 0 && !(await showConfirm(t('MAPART_NEW_CONFIRM')))) return;
        registry.clear();
        history.reset();
        syncHistory();
        setProjectId(newProjectId());
        setName(t('MAPART_UNTITLED'));
        setDoc(reconcile(emptyDocument()));
        setViewport({ ...DEFAULT_VIEWPORT });
        setSelectedIds([]);
        rememberLastOpened(null);
        bump();
    };

    const clearLayers = async () => {
        if (doc.layers.length === 0) return;
        if (!(await showConfirm(t('MAPART_CLEAR_CONFIRM'), true))) return;
        snapshot();
        endGesture();
        // Sources stay for the same reason delete keeps them: undo restores the layers, and it has no
        // way to bring a closed bitmap back.
        setDoc('layers', []);
        setSelectedIds([]);
        bump();
    };

    const applyPresetSelection = (selection: BlockSelection) => {
        const active = palette();
        if (!active) return;
        setSettings('selection', reconcile(sanitiseSelection(active, selection)));
        bump();
    };

    const saveCurrentPreset = (name: string) => {
        const active = palette();
        if (!active) return;
        const preset: BlockPreset = {
            id: crypto.randomUUID(),
            name,
            updatedAt: new Date().toISOString(),
            selection: sanitiseSelection(active, unwrap(settings).selection),
        };
        const next = upsertPreset(presets(), preset);
        setPresets(next);
        storePresets(next);
        toast?.success(t('MAPART_PRESETS_SAVED', preset.name));
    };

    const writePresets = (next: BlockPreset[]) => {
        setPresets(next);
        storePresets(next);
    };

    const updatePreset = (preset: BlockPreset) => {
        const active = palette();
        if (!active) return;
        writePresets(
            upsertPreset(presets(), {
                ...preset,
                updatedAt: new Date().toISOString(),
                selection: sanitiseSelection(active, unwrap(settings).selection),
            }),
        );
        toast?.success(t('MAPART_PRESETS_UPDATED', preset.name));
    };

    const renamePreset = (preset: BlockPreset, name: string) => {
        if (!name || name === preset.name) return;
        writePresets(upsertPreset(presets(), { ...preset, name, updatedAt: new Date().toISOString() }));
    };

    const deletePreset = async (preset: BlockPreset) => {
        if (!(await showConfirm(t('MAPART_PRESETS_DELETE_CONFIRM', preset.name), true))) return;
        const next = removePreset(presets(), preset.id);
        setPresets(next);
        storePresets(next);
    };

    const exportPresets = () => {
        saveBlob(new Blob([serialisePresets(presets())], { type: 'application/json' }), `${fileStem()}${PRESET_FILE_EXTENSION}`);
        toast?.success(t('MAPART_PRESETS_EXPORTED'));
    };

    const importPresets = async (file: File) => {
        const active = palette();
        if (!active) return;
        try {
            const imported = parsePresets(await file.text()).map((preset) => ({
                ...preset,
                selection: sanitiseSelection(active, preset.selection),
            }));
            const next = imported.reduce((list, preset) => upsertPreset(list, preset), presets());
            setPresets(next);
            storePresets(next);
            toast?.success(t('MAPART_PRESETS_IMPORTED', imported.length));
        } catch {
            toast?.error(t('MAPART_PRESETS_IMPORT_FAILED'));
        }
    };

    const openRecents = async () => {
        await refreshRecents();
        setRecentsOpen(true);
    };

    const openProject = async (id: string) => {
        const stored = await openStored(id);
        if (!stored) {
            toast?.error(t('MAPART_PROJECT_OPEN_FAILED'));
            return;
        }
        history.reset();
        syncHistory();
        await applyProject(stored);
        setRecentsOpen(false);
    };

    const removeProject = async (summary: ProjectSummary) => {
        if (!(await showConfirm(t('MAPART_PROJECT_DELETE_CONFIRM', summary.name), true))) return;
        await deleteProject(summary.id);
        await refreshRecents();
        if (summary.id === projectId()) await newProject();
    };

    const exportProject = async () => {
        const text = await serialiseProject(currentProject());
        saveBlob(new Blob([text], { type: 'application/json' }), `${fileStem()}${PROJECT_FILE_EXTENSION}`);
        toast?.success(t('MAPART_PROJECT_EXPORTED'));
    };

    const importProject = async (file: File) => {
        try {
            const project = parseProject(await file.text(), newProjectId());
            history.reset();
            syncHistory();
            await applyProject(project);
            await refreshRecents();
            toast?.success(t('MAPART_PROJECT_IMPORTED'));
        } catch {
            toast?.error(t('MAPART_PROJECT_IMPORT_FAILED'));
        }
    };

    const fileStem = () => (name().trim() || 'mapart').replace(/[^\w.-]+/g, '_');

    /** A pixel perfect image lands at the largest whole step filling half the artwork, since icons are tiny. */
    const addImageLayer = async (
        blob: Blob,
        label: string,
        options: { pixelPerfect: boolean; brightness: number } = { pixelPerfect: false, brightness: 1 },
    ) => {
        const bitmap = await createImageBitmap(blob);
        const sourceId = nextLayerId();
        registry.set(sourceId, bitmap, blob);
        const width = documentWidth(doc);
        const length = documentLength(doc);
        const scale = options.pixelPerfect
            ? fittingPixelScale(bitmap.width, bitmap.height, width / 2, length / 2)
            : Math.min(1, width / bitmap.width, length / bitmap.height);
        snapshot();
        const layer: Layer = {
            id: nextLayerId(),
            kind: 'image',
            name: label,
            visible: true,
            opacity: 1,
            x: Math.round((width - bitmap.width * scale) / 2),
            y: Math.round((length - bitmap.height * scale) / 2),
            scale,
            rotation: 0,
            ...defaultLayerSection('image'),
            sourceId,
            width: bitmap.width,
            height: bitmap.height,
            ...NEUTRAL_ADJUSTMENTS,
            brightness: options.brightness,
            pixelPerfect: options.pixelPerfect,
        };
        setDoc('layers', (layers) => [...layers, layer]);
        setSelectedIds([layer.id]);
        bump();
    };

    const addTextLayer = () => {
        snapshot();
        const layer: Layer = {
            id: nextLayerId(),
            kind: 'text',
            name: t('MAPART_LAYER_TEXT'),
            visible: true,
            opacity: 1,
            x: 8,
            y: 8,
            scale: 1,
            rotation: 0,
            ...defaultLayerSection('text'),
            text: t('MAPART_TEXT_DEFAULT'),
            fontSize: 24,
            fontFamily: TEXT_FONTS[0].family,
            fontWeight: DEFAULT_FONT_WEIGHT,
            fill: solidPaint('#ffffff'),
            stroke: noPaint(),
            strokeWidth: 0,
            edges: DEFAULT_TEXT_EDGES,
            edgeCutoff: DEFAULT_EDGE_CUTOFF,
            pixelSnap: true,
            monospace: false,
        };
        setDoc('layers', (layers) => [...layers, layer]);
        setSelectedIds([layer.id]);
        bump();
    };

    const addShapeLayer = () => {
        snapshot();
        const width = documentWidth(doc);
        const length = documentLength(doc);
        const size = Math.max(8, Math.round(Math.min(width, length) / 3));
        const layer: Layer = {
            id: nextLayerId(),
            kind: 'shape',
            name: t('MAPART_LAYER_SHAPE'),
            visible: true,
            opacity: 1,
            x: Math.round((width - size) / 2),
            y: Math.round((length - size) / 2),
            scale: 1,
            rotation: 0,
            ...defaultLayerSection('shape'),
            shape: 'rect',
            width: size,
            height: size,
            radius: 0,
            fill: gradientPaint('linear', '#ff8844', '#3355ff'),
            stroke: solidPaint('#ffffff'),
            strokeWidth: 1,
            edges: DEFAULT_TEXT_EDGES,
            edgeCutoff: DEFAULT_EDGE_CUTOFF,
        };
        setDoc('layers', (layers) => [...layers, layer]);
        setSelectedIds([layer.id]);
        bump();
    };

    /**
     * The one path every layer edit takes, so a group edit is the same code as a single one, and a
     * pixel perfect image lands back on its grid whichever control moved it.
     */
    const editLayers = (ids: string[], mutate: (layer: Layer) => void) => {
        if (ids.length === 0) return;
        beginGesture();
        for (const id of ids) {
            const index = doc.layers.findIndex((l) => l.id === id);
            if (index < 0) continue;
            setDoc('layers', index, produce((layer) => {
                mutate(layer);
                settlePixelPerfect(layer);
            }));
        }
        bump();
    };

    /** Members of a group hide and show together, so a group is never half visible. */
    const toggleVisible = (id: string) => {
        const layer = doc.layers.find((l) => l.id === id);
        if (!layer) return;
        const members = expandGroups(unwrap(doc).layers, [id]);
        const visible = !layer.visible;
        snapshot();
        endGesture();
        for (const member of members) {
            const index = doc.layers.findIndex((l) => l.id === member);
            if (index >= 0) setDoc('layers', index, 'visible', visible);
        }
        bump();
    };

    const reorderLayer = (id: string, direction: -1 | 1) => {
        const reordered = reorderBlock(unwrap(doc).layers, [id], direction);
        if (reordered === unwrap(doc).layers) return;
        snapshot();
        endGesture();
        setDoc('layers', reordered);
        bump();
    };

    const groupSelection = () => {
        const grouped = groupLayers(unwrap(doc).layers, selectedIds());
        if (grouped === unwrap(doc).layers) return;
        snapshot();
        endGesture();
        setDoc('layers', grouped);
        setSelectedIds(expandGroups(grouped, selectedIds()));
        bump();
    };

    const ungroupSelection = () => {
        const loose = ungroupLayers(unwrap(doc).layers, selectedIds());
        if (loose === unwrap(doc).layers) return;
        snapshot();
        endGesture();
        setDoc('layers', loose);
        bump();
    };

    const deleteLayers = async (ids: string[]) => {
        const doomed = doc.layers.filter((l) => expandGroups(unwrap(doc).layers, ids).includes(l.id));
        if (doomed.length === 0) return;
        const question =
            doomed.length === 1
                ? t('MAPART_LAYER_DELETE_CONFIRM', doomed[0].name)
                : t('MAPART_LAYERS_DELETE_CONFIRM', doomed.length);
        if (!(await showConfirm(question, true))) return;
        snapshot();
        endGesture();
        // The bitmap stays. Copies of an image layer share one source, so freeing it here wiped every
        // other instance, and undo could not bring it back either: history holds the document, not
        // the decoded image. Sources are dropped when saving instead, once nothing refers to them.
        const removed = new Set(doomed.map((l) => l.id));
        // Prune afterwards: a group whose other members are gone is no longer a group.
        setDoc('layers', (layers) => pruneGroups(layers.filter((l) => !removed.has(l.id))));
        setSelectedIds((ids) => ids.filter((id) => !removed.has(id)));
        bump();
    };

    const deleteSelected = () => deleteLayers(selectedIds());

    const layerBounds = (layer: Layer) =>
        layerBox(layer, (text: TextLayer) => {
            const ctx = composite.getContext('2d');
            if (!ctx) return 0;
            return textWidth(ctx, text);
        });

    const layerAt = (x: number, y: number): Layer | null => {
        for (let i = doc.layers.length - 1; i >= 0; i--) {
            const layer = doc.layers[i];
            if (!layer.visible) continue;
            const size = layerBounds(layer);
            const pad = PICK_PADDING;
            if (
                x >= layer.x + size.x - pad &&
                x <= layer.x + size.x + size.width + pad &&
                y >= layer.y + size.y - pad &&
                y <= layer.y + size.y + size.height + pad
            ) {
                return layer;
            }
        }
        return null;
    };

    /** Grabbing one member of a group grabs all of it, whether from the canvas or the layer list. */
    const membersOf = (id: string) => expandGroups(unwrap(doc).layers, [id]);

    /** The one path both the canvas and the layer list take, so groups behave the same in each. */
    const applySelection = (members: string[], additive: boolean) => {
        if (!additive) {
            setSelectedIds(members);
            return;
        }
        setSelectedIds((ids) =>
            members.every((member) => ids.includes(member))
                ? ids.filter((held) => !members.includes(held))
                : [...ids.filter((held) => !members.includes(held)), ...members],
        );
    };

    /** Reports whether a layer was grabbed, which is what tells the canvas to pan instead. */
    const pickLayer = (x: number, y: number, additive: boolean) => {
        const hit = layerAt(x, y);
        if (!hit) {
            // A plain click on empty canvas drops the selection; an additive one keeps what is held.
            if (!additive) setSelectedIds([]);
            return false;
        }
        const members = membersOf(hit.id);
        if (!additive) {
            // Press keeps the wider selection so a drag moves all of it; a click without travel
            // collapses to what is under the pointer, which is how you leave a multi selection.
            if (!members.every((member) => selectedIds().includes(member))) setSelectedIds(members);
            else collapseTo = members;
            return true;
        }
        applySelection(members, true);
        return true;
    };

    const selectLayer = (id: string, additive: boolean) => applySelection(membersOf(id), additive);

    const selectedLayers = () => {
        const ids = selectedIds();
        return doc.layers.filter((layer) => ids.includes(layer.id));
    };

    /** The primary layer, whose values the property editors show. */
    const selectedLayer = () => {
        const ids = selectedIds();
        if (ids.length === 0) return null;
        return doc.layers.find((layer) => layer.id === ids[ids.length - 1]) ?? null;
    };

    const boundsOf = (layer: Layer) => {
        const box = layerBounds(layer);
        return { x: layer.x + box.x, y: layer.y + box.y, width: box.width, height: box.height };
    };

    const selectionBox = (): SelectionBox | null => {
        const visible = selectedLayers().filter((layer) => layer.visible);
        if (visible.length === 0) return null;
        if (visible.length === 1) {
            const layer = visible[0];
            return {
                ...boundsOf(layer),
                rotation: layer.rotation,
                handles: layer.kind === 'shape' ? 'free' : 'uniform',
            };
        }

        // A group box cannot carry one rotation or one resize, so it only frames and moves.
        const boxes = visible.map(boundsOf);
        const left = Math.min(...boxes.map((b) => b.x));
        const top = Math.min(...boxes.map((b) => b.y));
        return {
            x: left,
            y: top,
            width: Math.max(...boxes.map((b) => b.x + b.width)) - left,
            height: Math.max(...boxes.map((b) => b.y + b.height)) - top,
            rotation: 0,
            handles: 'none',
        };
    };

    const selectionMembers = () => {
        const visible = selectedLayers().filter((layer) => layer.visible);
        if (visible.length < 2) return [];
        return visible.map((layer) => ({ ...boundsOf(layer), rotation: layer.rotation }));
    };

    /** Kind specific values only reach the layers that share the primary's kind. */
    const patchSelection = (values: Partial<Layer>) => {
        const primary = selectedLayer();
        if (!primary) return;
        const shared = isUniversalPatch(values);
        const targets = selectedLayers()
            .filter((layer) => shared || layer.kind === primary.kind)
            .map((layer) => layer.id);
        editLayers(targets, (layer) => Object.assign(layer, values));
    };

    /** Arrow keys walk the whole selection a block at a time; a burst folds into one undo step. */
    const nudgeSelection = (dx: number, dy: number) => {
        editLayers(selectedIds(), (layer) => {
            layer.x += dx;
            layer.y += dy;
        });
        window.clearTimeout(nudgeTimer);
        nudgeTimer = window.setTimeout(endGesture, 400);
    };

    /** Shifts each layer by its own amount, in one gesture so it undoes as a single step. */
    const shiftLayers = (axis: 'x' | 'y', deltas: Map<string, number>) => {
        if ([...deltas.values()].every((delta) => delta === 0)) return;
        editLayers([...deltas.keys()], (layer) => {
            layer[axis] += deltas.get(layer.id) ?? 0;
        });
    };

    /**
     * What aligning and distributing move around. A group counts as one unit, boxed by its members
     * together, so arranging shifts it as a block instead of tearing it apart.
     */
    const arrangeUnits = () => {
        const units = new Map<string, { ids: string[]; box: ReturnType<typeof boundsOf> }>();
        for (const layer of selectedLayers().filter((entry) => entry.visible)) {
            const box = boundsOf(layer);
            const unit = units.get(layer.groupId ?? layer.id);
            if (!unit) {
                units.set(layer.groupId ?? layer.id, { ids: [layer.id], box });
                continue;
            }
            unit.ids.push(layer.id);
            const right = Math.max(unit.box.x + unit.box.width, box.x + box.width);
            const bottom = Math.max(unit.box.y + unit.box.height, box.y + box.height);
            unit.box.x = Math.min(unit.box.x, box.x);
            unit.box.y = Math.min(unit.box.y, box.y);
            unit.box.width = right - unit.box.x;
            unit.box.height = bottom - unit.box.y;
        }
        return [...units.values()];
    };

    /**
     * Equal gaps between neighbours along one axis. The outermost two stay where they are, since they
     * are what the user positioned to define the span.
     */
    const distributeSelection = (axis: 'x' | 'y') => {
        const units = arrangeUnits();
        if (units.length < 3) return;
        const size = axis === 'x' ? 'width' : 'height';
        const ordered = [...units].sort((a, b) => a.box[axis] - b.box[axis]);
        const first = ordered[0].box;
        const last = ordered[ordered.length - 1].box;
        const span = last[axis] + last[size] - first[axis];
        const occupied = ordered.reduce((sum, entry) => sum + entry.box[size], 0);
        const gap = (span - occupied) / (ordered.length - 1);

        const deltas = new Map<string, number>();
        let cursor = first[axis];
        for (const unit of ordered) {
            const delta = Math.round(cursor - unit.box[axis]);
            for (const id of unit.ids) deltas.set(id, delta);
            cursor += unit.box[size] + gap;
        }
        shiftLayers(axis, deltas);
    };

    /** Every selected layer, or group, to one edge or the centre of the box they span together. */
    const alignSelection = (mode: 'start' | 'centre' | 'end', axis: 'x' | 'y') => {
        const units = arrangeUnits();
        const frame = selectionBox();
        if (units.length < 2 || !frame) return;
        const size = axis === 'x' ? 'width' : 'height';
        const deltas = new Map<string, number>();
        for (const unit of units) {
            const target =
                mode === 'start'
                    ? frame[axis]
                    : mode === 'end'
                      ? frame[axis] + frame[size] - unit.box[size]
                      : frame[axis] + (frame[size] - unit.box[size]) / 2;
            const delta = Math.round(target - unit.box[axis]);
            for (const id of unit.ids) deltas.set(id, delta);
        }
        shiftLayers(axis, deltas);
    };

    /** The primary and whatever it is grouped with, which is what the others are matched against. */
    const primaryUnit = () => {
        const primary = selectedLayer();
        if (!primary) return null;
        return arrangeUnits().find((unit) => unit.ids.includes(primary.id)) ?? null;
    };

    /** Every other selected layer, or group, gets the top left corner of the primary's box. */
    const matchPosition = () => {
        const anchor = primaryUnit();
        if (!anchor) return;
        const offsets = new Map<string, { x: number; y: number }>();
        for (const unit of arrangeUnits()) {
            if (unit === anchor) continue;
            const offset = { x: Math.round(anchor.box.x - unit.box.x), y: Math.round(anchor.box.y - unit.box.y) };
            for (const id of unit.ids) offsets.set(id, offset);
        }
        editLayers([...offsets.keys()], (layer) => {
            layer.x += offsets.get(layer.id)?.x ?? 0;
            layer.y += offsets.get(layer.id)?.y ?? 0;
        });
        endGesture();
    };

    /**
     * Shapes take the primary's box exactly. Images and text keep their aspect, so they grow until
     * they fill it along one side. The primary's own group is left alone, it is the reference.
     */
    const matchSize = () => {
        const anchor = primaryUnit();
        const primary = selectedLayer();
        if (!anchor || !primary) return;
        const target = boundsOf(primary);
        const resized = selectedLayers()
            .filter((layer) => layer.visible && !anchor.ids.includes(layer.id))
            .map((layer) => layer.id);
        editLayers(resized, (layer) => {
            if (layer.kind === 'shape') {
                const strokeWidth = layer.strokeWidth > 0 && layer.stroke.kind !== 'none' ? layer.strokeWidth : 0;
                layer.width = Math.max(1, target.width / layer.scale - strokeWidth);
                layer.height = Math.max(1, target.height / layer.scale - strokeWidth);
                return;
            }
            const box = layerBounds(layer);
            if (box.width <= 0 || box.height <= 0) return;
            const factor = Math.min(target.width / box.width, target.height / box.height);
            layer.scale = Math.max(0.01, Math.min(MAX_LAYER_SCALE, layer.scale * factor));
        });
        endGesture();
    };

    /** X and Z move the whole group, so a multi selection keeps its arrangement. */
    const moveSelection = (axis: 'x' | 'y', value: number) => {
        const box = selectionBox();
        if (box === null || !Number.isFinite(value)) return;
        const delta = value - (axis === 'x' ? box.x : box.y);
        if (delta === 0) return;
        editLayers(selectedIds(), (layer) => {
            layer[axis] += delta;
        });
    };

    /** Shapes take width and height directly; images and text scale, keeping their aspect. */
    const resizeSelected = (handle: ResizeHandle, dx: number, dy: number) => {
        const layer = selectedLayer();
        if (!layer) return;
        const index = doc.layers.findIndex((l) => l.id === layer.id);
        if (index < 0) return;
        const box = layerBounds(layer);
        if (box.width <= 0 || box.height <= 0) return;

        const west = handle.includes('w');
        const east = handle.includes('e');
        const north = handle.startsWith('n');
        const south = handle.startsWith('s');

        beginGesture();

        if (layer.kind === 'shape') {
            setDoc('layers', index, produce((draft) => {
                const shape = draft as Extract<Layer, { kind: 'shape' }>;
                if (east) shape.width = Math.max(1, shape.width + dx / shape.scale);
                if (west) {
                    const next = Math.max(1, shape.width - dx / shape.scale);
                    shape.x += (shape.width - next) * shape.scale;
                    shape.width = next;
                }
                if (south) shape.height = Math.max(1, shape.height + dy / shape.scale);
                if (north) {
                    const next = Math.max(1, shape.height - dy / shape.scale);
                    shape.y += (shape.height - next) * shape.scale;
                    shape.height = next;
                }
            }));
            bump();
            return;
        }

        const rawScale = resizeRawScale ?? layer.scale;
        const growth = (east ? dx : west ? -dx : 0) + (south ? dy : north ? -dy : 0);
        const rawBoxSpan = (box.width + box.height) * (rawScale / layer.scale);
        const factor = Math.max(0.05, 1 + growth / rawBoxSpan);
        const nextScale = Math.max(0.01, Math.min(MAX_LAYER_SCALE, rawScale * factor));
        resizeRawScale = nextScale;
        setDoc('layers', index, produce((draft) => {
            draft.scale = nextScale;
            settlePixelPerfect(draft);
        }));

        const grown = layerBounds(doc.layers[index]);
        setDoc('layers', index, produce((draft) => {
            if (west) draft.x += box.width - grown.width;
            if (north) draft.y += box.height - grown.height;
        }));
        bump();
    };

    /** Artwork frame, map seams, and every layer that is not being dragged. */
    const snapTargets = (moving: Set<string>): SnapAxes => {
        const frame = frameTargets(documentWidth(doc), documentLength(doc), MAP_SIZE);
        for (const other of doc.layers) {
            if (moving.has(other.id) || !other.visible) continue;
            const box = boundsOf(other);
            frame.xs.push(...boxEdges(box.x, box.width));
            frame.ys.push(...boxEdges(box.y, box.height));
        }
        return frame;
    };

    const dragSelected = (dx: number, dy: number, disableSnap: boolean) => {
        const ids = selectedIds();
        const box = selectionBox();
        if (ids.length === 0 || !box) return;
        beginGesture();

        // Track the unsnapped origin, or the selection sticks to a guide once it lands on one.
        if (!dragRaw) dragRaw = { x: box.x, y: box.y, fromX: box.x, fromY: box.y };
        dragRaw.x += dx;
        dragRaw.y += dy;

        // Measured from where the drag began, never from the current box: a snap moves the box, and
        // taking travel from the moved box folds that offset back in, so the candidate drifts away
        // from the pointer and which edge is close enough to snap turns erratic.
        // Round the travelled distance, never the result: rounding a snapped edge pushes it off.
        let nextX = dragRaw.fromX + Math.round(dragRaw.x - dragRaw.fromX);
        let nextY = dragRaw.fromY + Math.round(dragRaw.y - dragRaw.fromY);
        let guideX: number | null = null;
        let guideY: number | null = null;

        if (!disableSnap) {
            const targets = snapTargets(new Set(ids));
            const threshold = SNAP_SCREEN_PX / viewport().zoom;
            const horizontal = snapAxis(boxEdges(nextX, box.width), targets.xs, threshold);
            const vertical = snapAxis(boxEdges(nextY, box.height), targets.ys, threshold);
            nextX += horizontal.offset;
            nextY += vertical.offset;
            guideX = horizontal.guide;
            guideY = vertical.guide;
        }

        setGuides({ x: guideX, y: guideY });
        const deltaX = nextX - box.x;
        const deltaY = nextY - box.y;
        if (deltaX === 0 && deltaY === 0) return;
        editLayers(ids, (layer) => {
            layer.x += deltaX;
            layer.y += deltaY;
        });
    };

    const endDrag = (moved: boolean) => {
        if (!moved && collapseTo) setSelectedIds(collapseTo);
        collapseTo = null;
        dragRaw = null;
        resizeRawScale = null;
        setGuides({ x: null, y: null });
        endGesture();
    };

    const setMaps = (axis: 'mapsX' | 'mapsZ', value: number) => {
        const clamped = Math.min(MAX_MAPS, Math.max(1, Math.round(value)));
        if (doc[axis] === clamped) return;
        snapshot();
        endGesture();
        setDoc(axis, clamped);
        bump();
    };

    const setBackground = (value: string) => {
        beginGesture();
        setDoc('background', value);
        bump();
    };

    const identifierFor = (block: { id: number; meta: number }) => {
        const r = resolver();
        const def = r?.getItemById?.(block.id, block.meta) ?? r?.getItemById?.(block.id, 0);
        return def ? `minecraft:${def.name}:${block.meta}` : `minecraft:${block.id}`;
    };

    const onSupportBlockChange = (identifiers: string[]) => {
        const identifier = identifiers[identifiers.length - 1];
        if (!identifier) return;
        const r = resolver();
        const body = identifier.replace(/^minecraft:/, '');
        const match = body.match(/^(.+):(\d+)$/);
        const blockName = match ? match[1] : body;
        const meta = match ? Number(match[2]) : 0;
        const def = r?.getAllItems?.().find((item) => item.name === blockName && item.metadata === meta)
            ?? r?.getAllItems?.().find((item) => item.name === blockName);
        if (!def) {
            toast?.error(t('MAPART_SUPPORT_BLOCK_UNKNOWN'));
            return;
        }
        setSettings('supportBlock', { id: def.id, meta });
        bump();
    };

    const materialStack = (material: MaterialCount) => {
        const r = resolver();
        const def = r?.getItemById?.(material.id, material.meta) ?? r?.getItemById?.(material.id, 0);
        return fromIdentifier(def ? `minecraft:${def.name}` : `minecraft:${material.id}`, material.meta, r);
    };

    /** One region of the build as the layout input the schematic writer wants. */
    const layoutInput = (
        built: BuildResult,
        entries: ReturnType<typeof buildPaletteEntries>,
        region: BuildRegion,
    ) => ({
        width: region.width,
        length: region.length,
        pixels: cropArea(built.entries, built.width, region),
        entries,
        heights: cropArea(built.heights, built.width, region),
        nooblineHeights: region.nooblineHeights,
        support: {
            counts: cropArea(built.supportCounts, built.width, region),
            nooblineCounts: region.nooblineSupportCounts,
            total: built.supportCount,
        },
        supportBlock: unwrap(settings).supportBlock,
        maxY: region.maxY,
        includeNoobline: unwrap(settings).noobline,
    });

    /** The tones the build actually quantised with, which a flat or staircased section may have changed. */
    const layoutEntries = (active: MapartPalette, built: BuildResult) =>
        buildPaletteEntries(active, unwrap(settings).selection, built.paletteMode);

    const previewMesh = createMemo(() => {
        const active = palette();
        const built = result();
        if (view() !== '3d' || !active || !built) return null;
        const entries = layoutEntries(active, built);
        const builds = built.regions.map((region) => ({
            input: layoutInput(built, entries, region),
            column: region.x / MAP_SIZE,
            row: region.z / MAP_SIZE,
        }));
        return buildPreviewMesh(builds, active);
    });

    const exportSchematic = async () => {
        const active = palette();
        const built = result();
        if (!active || !built) return;
        if (built.schematicBytes > MAX_SCHEMATIC_BYTES) {
            toast?.error(t('MAPART_EXPORT_TOO_LARGE', Math.round(built.schematicBytes / (1024 * 1024))));
            return;
        }
        setExporting(true);
        try {
            const entries = layoutEntries(active, built);
            const encodeRegion = (region: BuildRegion) =>
                encodeSchematic(buildSchematicLayout(layoutInput(built, entries, region)));

            if (settings.exportMode !== 'tiles') {
                const bytes = await encodeRegion(built.regions[0]);
                saveBlob(new Blob([bytes as BlobPart], { type: 'application/octet-stream' }), `${fileStem()}.schematic`);
                toast?.success(t('MAPART_EXPORT_DONE'));
                setExporting(false);
                return;
            }

            const tileFiles: ZipEntry[] = [];
            for (const region of built.regions) {
                tileFiles.push({
                    name: `${fileStem()}_${region.x / MAP_SIZE}_${region.z / MAP_SIZE}.schematic`,
                    bytes: await encodeRegion(region),
                });
            }
            // One archive, not one download per tile: a browser drops the tail of a download burst,
            // which is why a 15 tile export arrived with 10 files in it.
            saveBlob(zipStore(tileFiles, new Date()), `${fileStem()}_tiles.zip`);
            toast?.success(t('MAPART_EXPORT_TILES_DONE', tileFiles.length));
        } catch {
            toast?.error(t('MAPART_EXPORT_FAILED'));
        }
        setExporting(false);
    };

    const onSurfaceSize = (width: number, height: number) => {
        surfaceWidth = width;
        surfaceHeight = height;
    };

    /** Whole artwork centred in the surface, so a big map is neither clipped nor lost off screen. */
    const fitView = () => {
        if (surfaceWidth === 0 || surfaceHeight === 0) return;
        setViewport(fit(documentWidth(doc), documentLength(doc), surfaceWidth, surfaceHeight, FIT_PADDING));
    };

    /** Buttons zoom about the middle of the surface, the way the wheel zooms about the cursor. */
    const zoomBy = (factor: number) =>
        setViewport((current) => zoomAt(current, factor, surfaceWidth / 2, surfaceHeight / 2));

    const heightSpan = () => {
        const built = result();
        return built ? built.maxY - built.minY + 1 : 0;
    };

    const formatError = (value: number | undefined) => (value ?? 0).toFixed(1);

    return (
        <div
            class={styles.container}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer?.files?.[0];
                if (file?.type.startsWith('image/')) void addImageLayer(file, file.name);
            }}
        >
            <div class={styles.headerRow}>
                <h1><Text val="MAPART_TITLE" /></h1>
                <Show when={result()}>
                    <span class={styles.entryCount}>
                        {result()!.width} × {result()!.length} <Text val="MAPART_BLOCKS" />
                    </span>
                </Show>
            </div>
            <p class={styles.subtitle}><Text val="MAPART_SUBTITLE" /></p>

            <Show when={!loading()} fallback={
                <div class={styles.grid}>
                    <For each={[0, 1, 2]}>{() => <div class={styles.skeletonCard} />}</For>
                </div>
            }>
                <div class={`fcard ${styles.toolbar}`}>
                    <div class={styles.toolbarRow}>
                        <input
                            ref={projectInput}
                            type="file"
                            accept=".json,application/json"
                            class={styles.hiddenInput}
                            onChange={(e) => {
                                const file = e.currentTarget.files?.[0];
                                if (file) void importProject(file);
                                e.currentTarget.value = '';
                            }}
                        />
                        <button type="button" class="btn btn-secondary" onClick={() => void newProject()}>
                            <i class="fa-solid fa-file" /> <Text val="MAPART_NEW" />
                        </button>
                        <button type="button" class="btn btn-secondary" onClick={() => void openRecents()}>
                            <i class="fa-solid fa-folder-open" /> <Text val="MAPART_OPEN" />
                        </button>
                        <button type="button" class="btn btn-secondary" onClick={() => void exportProject()}>
                            <i class="fa-solid fa-file-arrow-down" /> <Text val="MAPART_PROJECT_EXPORT" />
                        </button>
                        <button type="button" class="btn btn-secondary" onClick={() => projectInput?.click()}>
                            <i class="fa-solid fa-file-arrow-up" /> <Text val="MAPART_PROJECT_IMPORT" />
                        </button>
                        <button
                            type="button"
                            class="btn btn-secondary"
                            disabled={doc.layers.length === 0}
                            onClick={() => void clearLayers()}
                        >
                            <i class="fa-solid fa-eraser" /> <Text val="MAPART_CLEAR" />
                        </button>

                        <span class={styles.toolbarDivider} />

                        <input
                            ref={fileInput}
                            type="file"
                            accept="image/*"
                            class={styles.hiddenInput}
                            onChange={(e) => {
                                const file = e.currentTarget.files?.[0];
                                if (file) void addImageLayer(file, file.name);
                                e.currentTarget.value = '';
                            }}
                        />
                        <button type="button" class="btn btn-secondary" onClick={() => fileInput?.click()}>
                            <i class="fa-solid fa-image" /> <Text val="MAPART_ADD_IMAGE" />
                        </button>
                        <button type="button" class="btn btn-secondary" onClick={() => setIconsOpen(true)}>
                            <i class="fa-solid fa-cube" /> <Text val="MAPART_ADD_ICON" />
                        </button>
                        <button type="button" class="btn btn-secondary" onClick={addTextLayer}>
                            <i class="fa-solid fa-font" /> <Text val="MAPART_ADD_TEXT" />
                        </button>
                        <button type="button" class="btn btn-secondary" onClick={addShapeLayer}>
                            <i class="fa-solid fa-shapes" /> <Text val="MAPART_ADD_SHAPE" />
                        </button>
                        <button
                            type="button"
                            class="btn btn-secondary"
                            title={t('MAPART_UNDO')}
                            disabled={!canUndo()}
                            onClick={undo}
                        >
                            <i class="fa-solid fa-rotate-left" /> <Text val="MAPART_UNDO" />
                        </button>
                        <button
                            type="button"
                            class="btn btn-secondary"
                            title={t('MAPART_REDO')}
                            disabled={!canRedo()}
                            onClick={redo}
                        >
                            <i class="fa-solid fa-rotate-right" /> <Text val="MAPART_REDO" />
                        </button>

                        <div class={styles.toolbarEnd}>
                            <select
                                title={t('MAPART_EXPORT_MODE')}
                                value={settings.exportMode}
                                onChange={(e) => { setSettings('exportMode', e.currentTarget.value as ExportMode); bump(); }}
                            >
                                <option value="whole">{t('MAPART_EXPORT_WHOLE')}</option>
                                <option value="tiles">{t('MAPART_EXPORT_TILES')}</option>
                            </select>
                            <button
                                type="button"
                                class="btn btn-primary"
                                disabled={exporting() || !result() || result()!.blockCount === 0}
                                onClick={() => void exportSchematic()}
                            >
                                <Show when={exporting()} fallback={<><i class="fa-solid fa-download" /> <Text val="MAPART_EXPORT" /></>}>
                                    <i class="fa-solid fa-spinner fa-spin" />
                                </Show>
                            </button>
                        </div>
                    </div>
                </div>

                <div
                    class={styles.grid}
                    classList={{
                        [styles.gridNoLeft]: !leftOpen() && rightOpen(),
                        [styles.gridNoRight]: leftOpen() && !rightOpen(),
                        [styles.gridNoSides]: !leftOpen() && !rightOpen(),
                    }}
                >
                    <Show when={leftOpen()}>
                    <div class="v" style={{ gap: 'var(--spacing-lg)' }}>
                        <div class={`fcard v ${styles.panel}`}>
                            <span class={styles.groupLabel}><Text val="MAPART_ARTWORK" /></span>

                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="MAPART_NAME" /></span>
                                <input type="text" value={name()} onInput={(e) => setName(e.currentTarget.value)} />
                            </label>

                            <div class={styles.pair}>
                                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                    <span class={styles.fieldLabel}><Text val="MAPART_MAPS_X" /></span>
                                    <input
                                        type="number"
                                        min="1"
                                        max={MAX_MAPS}
                                        value={doc.mapsX}
                                        onChange={(e) => setMaps('mapsX', Number(e.currentTarget.value))}
                                    />
                                </label>
                                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                    <span class={styles.fieldLabel}><Text val="MAPART_MAPS_Z" /></span>
                                    <input
                                        type="number"
                                        min="1"
                                        max={MAX_MAPS}
                                        value={doc.mapsZ}
                                        onChange={(e) => setMaps('mapsZ', Number(e.currentTarget.value))}
                                    />
                                </label>
                            </div>
                        </div>

                        <div class={`fcard v ${styles.panel}`}>
                            <span class={styles.groupLabel}><Text val="MAPART_BUILD_SETTINGS" /></span>

                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="MAPART_STAIRCASE" /></span>
                                <select
                                    value={settings.staircase}
                                    onChange={(e) => { setSettings('staircase', e.currentTarget.value as StaircaseMode); bump(); }}
                                >
                                    <option value="off">{t('MAPART_STAIRCASE_OFF')}</option>
                                    <option value="classic">{t('MAPART_STAIRCASE_CLASSIC')}</option>
                                </select>
                            </label>

                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="MAPART_SUPPORT" /></span>
                                <select
                                    value={settings.support}
                                    onChange={(e) => { setSettings('support', e.currentTarget.value as SupportMode); bump(); }}
                                >
                                    <option value="none">{t('MAPART_SUPPORT_NONE')}</option>
                                    <option value="gravity">{t('MAPART_SUPPORT_GRAVITY')}</option>
                                    <option value="all">{t('MAPART_SUPPORT_ALL')}</option>
                                </select>
                            </label>

                            <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="MAPART_SUPPORT_BLOCK" /></span>
                                <ItemIdentifierPicker
                                    value={[identifierFor(settings.supportBlock)]}
                                    onChange={onSupportBlockChange}
                                    metaAware={true}
                                    single={true}
                                    disabled={settings.support === 'none'}
                                    maxResults={8}
                                    label={t('MAPART_SUPPORT_BLOCK')}
                                />
                            </div>

                            <label class={styles.checkRow} title={t('MAPART_NOOBLINE_HINT')}>
                                <input
                                    type="checkbox"
                                    checked={settings.noobline}
                                    onChange={(e) => { setSettings('noobline', e.currentTarget.checked); bump(); }}
                                />
                                <span class={styles.fieldLabel}><Text val="MAPART_NOOBLINE" /></span>
                            </label>
                            <Show when={!settings.noobline}>
                                <p class={styles.hint}>
                                    <i class="fa-solid fa-circle-info" /> <Text val="MAPART_NOOBLINE_OFF_HINT" />
                                </p>
                            </Show>

                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="MAPART_DITHER" /></span>
                                <select
                                    value={settings.dither}
                                    onChange={(e) => { setSettings('dither', e.currentTarget.value); bump(); }}
                                >
                                    <For each={DITHER_METHODS}>{(method) => (
                                        <option value={method.key}>
                                            {method.label ?? t('MAPART_DITHER_NONE')}
                                        </option>
                                    )}</For>
                                </select>
                            </label>

                            <Show when={settings.dither !== 'none'}>
                                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                    <span class={styles.fieldLabel}>
                                        <Text val="MAPART_DITHER_STRENGTH" />{' '}
                                        <span class={styles.value}>{Math.round(settings.ditherStrength * 100)}%</span>
                                    </span>
                                    <div class={styles.sliderRow}>
                                        <input
                                            type="range"
                                            min="0"
                                            max="1"
                                            step="0.05"
                                            value={settings.ditherStrength}
                                            onInput={(e) => { setSettings('ditherStrength', Number(e.currentTarget.value)); bump(); }}
                                        />
                                        <input
                                            type="number"
                                            min="0"
                                            max="1"
                                            step="0.05"
                                            value={Math.round(settings.ditherStrength * 100) / 100}
                                            onInput={(e) => {
                                                const next = Number(e.currentTarget.value);
                                                if (Number.isFinite(next)) { setSettings('ditherStrength', next); bump(); }
                                            }}
                                        />
                                    </div>
                                </label>

                                <label class={styles.checkRow}>
                                    <input
                                        type="checkbox"
                                        checked={settings.serpentine}
                                        onChange={(e) => { setSettings('serpentine', e.currentTarget.checked); bump(); }}
                                    />
                                    <span class={styles.fieldLabel}><Text val="MAPART_SERPENTINE" /></span>
                                </label>
                            </Show>

                            <label class={styles.checkRow} title={t('MAPART_TONE_GAMUT_HINT')}>
                                <input
                                    type="checkbox"
                                    checked={settings.toneMap.gamut}
                                    onChange={(e) => { setSettings('toneMap', 'gamut', e.currentTarget.checked); bump(); }}
                                />
                                <span class={styles.fieldLabel}><Text val="MAPART_TONE_GAMUT" /></span>
                            </label>

                            <label class={styles.checkRow} title={t('MAPART_TONE_LIGHTNESS_HINT')}>
                                <input
                                    type="checkbox"
                                    checked={settings.toneMap.lightness}
                                    onChange={(e) => { setSettings('toneMap', 'lightness', e.currentTarget.checked); bump(); }}
                                />
                                <span class={styles.fieldLabel}><Text val="MAPART_TONE_LIGHTNESS" /></span>
                            </label>

                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="MAPART_METRIC" /></span>
                                <select
                                    value={settings.metric}
                                    onChange={(e) => { setSettings('metric', e.currentTarget.value as ColourMetric); bump(); }}
                                >
                                    <option value="de2000">{t('MAPART_METRIC_DE2000')}</option>
                                    <option value="lab">{t('MAPART_METRIC_LAB')}</option>
                                    <option value="rgb">{t('MAPART_METRIC_RGB')}</option>
                                </select>
                            </label>

                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}>
                                    <Text val="MAPART_ALPHA" /> <span class={styles.value}>{settings.alphaThreshold}</span>
                                </span>
                                <input
                                    type="range"
                                    min="1"
                                    max="255"
                                    value={settings.alphaThreshold}
                                    onInput={(e) => { setSettings('alphaThreshold', Number(e.currentTarget.value)); bump(); }}
                                />
                            </label>

                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="MAPART_BACKGROUND" /></span>
                                <div class="h" style={{ gap: 'var(--spacing-sm)' }}>
                                    <input
                                        type="color"
                                        value={doc.background === TRANSPARENT ? '#000000' : doc.background}
                                        onInput={(e) => setBackground(e.currentTarget.value)}
                                        onChange={() => endGesture()}
                                    />
                                    <button
                                        type="button"
                                        class="btn btn-secondary btn-sm"
                                        onClick={() => { setBackground(TRANSPARENT); endGesture(); }}
                                    >
                                        <Text val="MAPART_BACKGROUND_NONE" />
                                    </button>
                                </div>
                            </label>
                        </div>

                        <div class={`fcard ${styles.panel}`}>
                            <PalettePicker
                                palette={palette()!}
                                selection={settings.selection}
                                onChange={(colourId, blockIndex) => { setSettings('selection', colourId, blockIndex); bump(); }}
                                onManagePresets={() => setPresetsOpen(true)}
                                onPreset={(preset) => {
                                    // reconcile, not a plain object: the store setter would merge and never clear a colour.
                                    setSettings('selection', reconcile(selectionForPreset(palette()!, preset)));
                                    bump();
                                }}
                            />
                        </div>
                    </div>
                    </Show>

                    <div class={`v ${styles.stage}`} style={{ gap: 'var(--spacing-lg)' }}>
                        <div class={`fcard v ${styles.panel}`}>
                            <div class={styles.canvasBar}>
                                <button
                                    type="button"
                                    class="btn btn-secondary btn-sm btn-icon"
                                    title={t(leftOpen() ? 'MAPART_PANEL_HIDE_LEFT' : 'MAPART_PANEL_SHOW_LEFT')}
                                    onClick={() => setLeftOpen((open) => !open)}
                                >
                                    <i class={leftOpen() ? 'fa-solid fa-angles-left' : 'fa-solid fa-angles-right'} />
                                </button>
                                <Show when={view() !== '3d'}>
                                    <div class="h" style={{ gap: 'var(--spacing-xs)' }}>
                                        <button
                                            type="button"
                                            class="btn btn-secondary btn-sm btn-icon"
                                            title={t('MAPART_ZOOM_OUT')}
                                            onClick={() => zoomBy(1 / ZOOM_STEP)}
                                        >
                                            <i class="fa-solid fa-magnifying-glass-minus" />
                                        </button>
                                        <span class={styles.zoomValue}>{Math.round(viewport().zoom * 100)}%</span>
                                        <button
                                            type="button"
                                            class="btn btn-secondary btn-sm btn-icon"
                                            title={t('MAPART_ZOOM_IN')}
                                            onClick={() => zoomBy(ZOOM_STEP)}
                                        >
                                            <i class="fa-solid fa-magnifying-glass-plus" />
                                        </button>
                                        <button
                                            type="button"
                                            class="btn btn-secondary btn-sm"
                                            title={t('MAPART_ZOOM_ACTUAL')}
                                            onClick={() => zoomBy(1 / viewport().zoom)}
                                        >
                                            1:1
                                        </button>
                                    </div>
                                </Show>
                                <div class="h" style={{ gap: 'var(--spacing-xs)' }}>
                                    <For each={[
                                        { key: 'both', label: 'MAPART_VIEW_BOTH' },
                                        { key: 'image', label: 'MAPART_VIEW_IMAGE' },
                                        { key: 'map', label: 'MAPART_VIEW_MAP' },
                                        { key: '3d', label: 'MAPART_VIEW_3D' },
                                    ] as const}>{(option) => (
                                        <button
                                            type="button"
                                            class="btn btn-secondary btn-sm"
                                            classList={{ [styles.activeButton]: view() === option.key }}
                                            onClick={() => setView(option.key)}
                                        >
                                            {t(option.label)}
                                        </button>
                                    )}</For>
                                </div>
                                <Show when={busy()}>
                                    <i class="fa-solid fa-spinner fa-spin" />
                                </Show>

                                <div class={styles.canvasBarEnd}>
                                    <Show when={view() !== '3d'}>
                                        <button type="button" class="btn btn-secondary btn-sm" onClick={fitView}>
                                            <i class="fa-solid fa-expand" /> <Text val="MAPART_ZOOM_FIT" />
                                        </button>
                                    </Show>
                                    <button
                                        type="button"
                                        class="btn btn-secondary btn-sm btn-icon"
                                        title={t(rightOpen() ? 'MAPART_PANEL_HIDE_RIGHT' : 'MAPART_PANEL_SHOW_RIGHT')}
                                        onClick={() => setRightOpen((open) => !open)}
                                    >
                                        <i class={rightOpen() ? 'fa-solid fa-angles-right' : 'fa-solid fa-angles-left'} />
                                    </button>
                                </div>
                            </div>

                            <Show when={doc.layers.length === 0}>
                                <p class={styles.hint}>
                                    <i class="fa-solid fa-paste" /> <Text val="MAPART_DROP_HINT" />
                                </p>
                            </Show>
                            <p class={styles.hint}>
                                <i class={view() === '3d' ? 'fa-solid fa-cube' : 'fa-solid fa-arrows-up-down-left-right'} />{' '}
                                <Text val={view() === '3d' ? 'MAPART_PREVIEW_HINT' : 'MAPART_CANVAS_HINT'} />
                            </p>

                            <div class={styles.canvasHost}>
                                <MapartCanvas
                                    composite={composite}
                                    result={result()}
                                    width={doc.mapsX * MAP_SIZE}
                                    length={doc.mapsZ * MAP_SIZE}
                                    viewport={viewport()}
                                    onViewport={setViewport}
                                    onSurfaceSize={onSurfaceSize}
                                    view={view()}
                                    mesh={previewMesh()}
                                    busy={busy()}
                                    mapsX={doc.mapsX}
                                    mapsZ={doc.mapsZ}
                                    showGrid={doc.mapsX > 1 || doc.mapsZ > 1}
                                    compositeRevision={compositeRevision()}
                                    selection={selectionBox()}
                                    members={selectionMembers()}
                                    onPick={pickLayer}
                                    onResize={resizeSelected}
                                    onDrag={dragSelected}
                                    onDragEnd={endDrag}
                                    guides={guides()}
                                />
                            </div>
                        </div>
                    </div>

                    <Show when={rightOpen()}>
                    <div class="v" style={{ gap: 'var(--spacing-lg)' }}>
                        <div class={`fcard ${styles.panel}`}>
                            <LayerPanel
                                layers={doc.layers}
                                selectedIds={selectedIds()}
                                box={selectionBox()}
                                onSelect={selectLayer}
                                onToggleVisible={toggleVisible}
                                onReorder={reorderLayer}
                                onDelete={(id) => void deleteLayers([id])}
                                onPatch={patchSelection}
                                onMove={moveSelection}
                                onAlign={alignSelection}
                                onMatchPosition={matchPosition}
                                onMatchSize={matchSize}
                                onDistribute={distributeSelection}
                                unitCount={arrangeUnits().length}
                                onGroup={groupSelection}
                                onUngroup={ungroupSelection}
                                onCommit={endGesture}
                            />
                        </div>

                        <div class={`fcard v ${styles.panel}`}>
                            <span class={styles.groupLabel}><Text val="MAPART_STATS" /></span>
                            <div class={styles.stats}>
                                <div class={styles.stat}>
                                    <span class={styles.fieldLabel}><Text val="MAPART_STAT_BLOCKS" /></span>
                                    <span class={styles.value}>{result()?.blockCount ?? 0}</span>
                                </div>
                                <div class={styles.stat}>
                                    <span class={styles.fieldLabel}><Text val="MAPART_STAT_SUPPORT" /></span>
                                    <span class={styles.value}>{result()?.supportCount ?? 0}</span>
                                </div>
                                <div class={styles.stat}>
                                    <span class={styles.fieldLabel}><Text val="MAPART_STAT_HEIGHT" /></span>
                                    <span class={styles.value}>{heightSpan()}</span>
                                </div>
                                <Show when={result()?.tone}>
                                    <div class={`${styles.stat} ${styles.statWide}`}>
                                        <span class={styles.fieldLabel}><Text val="MAPART_STAT_TONE" /></span>
                                        <span class={styles.value}>
                                            {t(
                                                'MAPART_STAT_TONE_VALUE',
                                                Math.round(result()!.tone!.clamped * 100),
                                                Math.round(result()!.tone!.paletteMinL),
                                                Math.round(result()!.tone!.paletteMaxL),
                                            )}
                                        </span>
                                    </div>
                                </Show>
                                <div class={`${styles.stat} ${styles.statWide}`}>
                                    <span class={styles.fieldLabel}><Text val="MAPART_STAT_ERROR" /></span>
                                    <span class={styles.value} title={t('MAPART_STAT_ERROR_HINT')}>
                                        {t('MAPART_STAT_ERROR_VALUE', formatError(result()?.error.mean), formatError(result()?.error.p95))}
                                    </span>
                                </div>
                            </div>

                            <Show when={result()?.warnings.length}>
                                <div class={styles.warnings}>
                                    <For each={result()!.warnings}>{(warning) => (
                                        <div class={styles.warning}>
                                            <i class="fa-solid fa-triangle-exclamation" />
                                            <span>{t(warning.key, ...(warning.args ?? []))}</span>
                                        </div>
                                    )}</For>
                                </div>
                            </Show>

                            <span class={styles.groupLabel}><Text val="MAPART_MATERIALS" /></span>
                            <Show when={result()?.materials.length} fallback={
                                <p class={styles.hint}>
                                    <i class="fa-solid fa-cubes" /> <Text val="MAPART_MATERIALS_EMPTY" />
                                </p>
                            }>
                                <div class={styles.materials}>
                                    <For each={result()!.materials}>{(material) => {
                                        const stack = createMemo(() => materialStack(material));
                                        return (
                                            <div class={styles.materialRow}>
                                                <ItemStackView stack={stack()} />
                                                <span class={styles.materialName}>{stackName(stack())}</span>
                                                <span class={styles.value}>× {material.count}</span>
                                            </div>
                                        );
                                    }}</For>
                                </div>
                            </Show>
                        </div>
                    </div>
                    </Show>
                </div>
            </Show>

            <input
                ref={presetInput}
                type="file"
                accept=".json,application/json"
                class={styles.hiddenInput}
                onChange={(e) => {
                    const file = e.currentTarget.files?.[0];
                    if (file) void importPresets(file);
                    e.currentTarget.value = '';
                }}
            />

            <PresetDialog
                open={presetsOpen()}
                presets={presets()}
                colourCount={palette() ? Object.keys(palette()!.colors).length - 1 : 0}
                currentCount={enabledCount(settings.selection)}
                onClose={() => setPresetsOpen(false)}
                onApply={(preset) => applyPresetSelection(preset.selection)}
                onSave={saveCurrentPreset}
                onUpdate={updatePreset}
                onRename={renamePreset}
                onDelete={(preset) => void deletePreset(preset)}
                onExport={exportPresets}
                onImport={() => presetInput?.click()}
            />

            <IconDialog
                open={iconsOpen()}
                onClose={() => setIconsOpen(false)}
                onInsert={(blob, label, brightness) => {
                    setIconsOpen(false);
                    void addImageLayer(blob, label, { pixelPerfect: true, brightness });
                }}
            />

            <Show when={recentsOpen()}>
                <Modal open={true} onClose={() => setRecentsOpen(false)} maxWidth="30rem" title={<Text val="MAPART_RECENTS" />}>
                    <Show when={recents().length > 0} fallback={
                        <EmptyState illustration="generic" title={t('MAPART_RECENTS_EMPTY')} hint={t('MAPART_RECENTS_EMPTY_HINT')} />
                    }>
                        <div class={styles.recents}>
                            <For each={recents()}>{(summary) => (
                                <div
                                    class={styles.recentRow}
                                    classList={{ [styles.recentActive]: summary.id === projectId() }}
                                    onClick={() => void openProject(summary.id)}
                                >
                                    <div class={styles.recentInfo}>
                                        <span class={styles.recentName}>{summary.name}</span>
                                        <span class={styles.fieldLabel}>{formatDateTime(summary.updatedAt)}</span>
                                    </div>
                                    <button
                                        type="button"
                                        class="btn btn-danger btn-sm btn-icon"
                                        title={t('DELETE')}
                                        onClick={() => void removeProject(summary)}
                                    >
                                        <i class="fa-solid fa-trash" />
                                    </button>
                                </div>
                            )}</For>
                        </div>
                    </Show>
                </Modal>
            </Show>
        </div>
    );
}
